import { Button } from "@/components/ui/button";
import { Download, FileText, File, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ExportButtonsProps {
  onExportPDF: (fileName?: string) => void;
  onExportDOCX: (fileName?: string) => void | Promise<void>;
  onCopyText: () => void;
  disabled?: boolean;
  defaultFileName?: string;
}

export const ExportButtons = ({
  onExportPDF,
  onExportDOCX,
  onCopyText,
  disabled,
  defaultFileName,
}: ExportButtonsProps) => {
  const [copied, setCopied] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [pendingType, setPendingType] = useState<"pdf" | "docx" | null>(null);

  const handleCopy = () => {
    onCopyText();
    setCopied(true);
    toast.success("Resume copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const openRename = (type: "pdf" | "docx") => {
    setPendingType(type);
    setRenameValue((defaultFileName || "Resume").trim());
    setRenameOpen(true);
  };

  const confirmRename = async () => {
    const name = renameValue.trim();
    setRenameOpen(false);
    if (!pendingType) return;
    if (pendingType === "pdf") onExportPDF(name);
    if (pendingType === "docx") await onExportDOCX(name);
    setPendingType(null);
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="default"
        size="sm"
        onClick={() => openRename("pdf")}
        disabled={disabled}
        className="gap-1.5"
      >
        <FileText className="w-4 h-4" />
        PDF
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => openRename("docx")}
        disabled={disabled}
        className="gap-1.5"
      >
        <File className="w-4 h-4" />
        DOCX
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        disabled={disabled}
        className="gap-1.5"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            Copied
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            Copy Text
          </>
        )}
      </Button>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name your download</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="export-name">File name</Label>
              <Input
                id="export-name"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="e.g., Umer_Resume_Jan_2026"
              />
              <p className="text-xs text-muted-foreground">We’ll add .pdf or .docx automatically.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenameOpen(false)}>
                Cancel
              </Button>
              <Button onClick={confirmRename} disabled={!renameValue.trim()}>
                Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
